// src/mobile_pages/quality/components/MobileQualityCAView.jsx
// 모바일 전용 품질 CAR(시정조치요구서) 뷰

import React from "react";
import { Calendar, AlertCircle, Target, Shield, CheckSquare, Link } from "lucide-react";
import PeopleView from "../../../components/common/people/PeopleView";

export default function MobileQualityCAView({ data }) {
    if (!data) return null;

    const header = data.header || {};
    const car = data.car || {};

    // 중요도에 따른 색상
    const getSeverityStyle = (severity) => {
        const s = (severity || "").toLowerCase();
        if (s.includes("높") || s.includes("high") || s.includes("심각")) {
            return "bg-red-50 text-red-700 border-red-200";
        }
        if (s.includes("중") || s.includes("medium")) {
            return "bg-amber-50 text-amber-700 border-amber-200";
        }
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
    };

    return (
        <div className="space-y-4">

            {/* 기본 정보 카드 */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
                    <div className="flex items-center gap-2">
                        <AlertCircle size={16} className="text-amber-600" />
                        <h3 className="text-sm font-semibold text-amber-700">시정조치요구서</h3>
                    </div>
                </div>
                <div className="p-4 space-y-3">
                    {/* 연결 NCR */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <Link size={16} className="text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">연결 NCR</p>
                            <p className="text-sm text-blue-600 font-medium break-words">
                                {header.ncr_dns || "-"}
                            </p>
                        </div>
                    </div>

                    {/* 중요도 */}
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                            <AlertCircle size={16} className="text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-400 mb-0.5">중요도</p>
                            <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${getSeverityStyle(header.severity)}`}>
                                {header.severity || "미지정"}
                            </span>
                        </div>
                    </div>

                    {/* 조치기한 / 종결일 */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                        <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-slate-400" />
                            <div>
                                <p className="text-xs text-slate-400">조치기한</p>
                                <p className="text-sm text-slate-700 font-medium">{header.due_date || "-"}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckSquare size={14} className="text-slate-400" />
                            <div>
                                <p className="text-xs text-slate-400">종결일</p>
                                <p className="text-sm text-slate-700 font-medium">{header.close_date || "-"}</p>
                            </div>
                        </div>
                    </div>

                    {/* 책임 담당자 */}
                    <div className="pt-2 border-t border-slate-100">
                        <p className="text-xs text-slate-400 mb-2">책임 담당자</p>
                        <PeopleView
                            dns={data.doc_dns}
                            placeholder="지정된 담당자 없음"
                        />
                    </div>
                </div>
            </div>

            {/* 조치 내용 */}
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-700">조치 내용</h3>
                </div>
                <div className="p-4 space-y-4">
                    {/* 근본원인 */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Target size={14} className="text-red-500" />
                            <p className="text-xs font-medium text-slate-600">근본원인</p>
                        </div>
                        <div className="rounded-xl bg-red-50/50 border border-red-100 p-3 text-sm text-slate-700 break-words">
                            {car.root_cause || "내용이 없습니다."}
                        </div>
                    </div>

                    {/* 시정조치 */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <AlertCircle size={14} className="text-amber-500" />
                            <p className="text-xs font-medium text-slate-600">시정조치</p>
                        </div>
                        <div className="rounded-xl bg-amber-50/50 border border-amber-100 p-3 text-sm text-slate-700 break-words">
                            {car.corrective_action || "내용이 없습니다."}
                        </div>
                    </div>

                    {/* 예방조치 */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Shield size={14} className="text-emerald-500" />
                            <p className="text-xs font-medium text-slate-600">예방조치</p>
                        </div>
                        <div className="rounded-xl bg-emerald-50/50 border border-emerald-100 p-3 text-sm text-slate-700 break-words">
                            {car.preventive_action || "내용이 없습니다."}
                        </div>
                    </div>

                    {/* 검증방법/기준 */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <CheckSquare size={14} className="text-blue-500" />
                            <p className="text-xs font-medium text-slate-600">검증방법/기준</p>
                        </div>
                        <div className="rounded-xl bg-blue-50/50 border border-blue-100 p-3 text-sm text-slate-700 break-words">
                            {car.verification || "내용이 없습니다."}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
